import type {
  AnalysisRun,
  AnalysisSchedule,
  CausalFilterDto,
  CausalProfile,
  MonthlyQuota,
  RunInputParameters,
  ScheduleFrequency,
} from './impact.types'

export type ProfilesPayload = CausalProfile[] | { profiles: CausalProfile[] }
export type SchedulesPayload = AnalysisSchedule[] | { schedules: AnalysisSchedule[] }
export type RunsPayload = AnalysisRun[] | { results: AnalysisRun[] }

export interface ProfileOverrideBody {
  global_profile: number
  is_active: true
  override_config: {
    fanout: {
      target_controls: { target_metric: string; control_metrics: string[] }
      pre_days: number | null
      post_days: number | null
      min_clicks_total: number | null
      min_days_after_intervention: number | null
      min_unique_days: number | null
      min_post_unique_days: number | null
    }
    data_source: { filters: CausalFilterDto[] }
  }
}

/**
 * Confirmed against a live response: there is no `effective_config` — the override endpoint
 * echoes back only the override delta it stored, under the same `override_config` shape the
 * request body sent (`fanout`/`data_source`). It carries none of the base profile's other config
 * (`level`, `input_params`, `change_metric`, …), so the caller must merge, never replace.
 */
export interface ProfileOverrideResult {
  id?: number
  override_config: ProfileOverrideBody['override_config']
}

/** POST/PUT schedule shape. A one-time cadence still creates and enqueues a schedule. */
export interface RunRequestBody {
  name: string
  profile_slugs: (string | null)[]
  input_parameters: RunInputParameters
  frequency: ScheduleFrequency
  // Omitted = uncapped (legacy FE-W5: `runAnalysis()` only sends this when > 0).
  max_credits?: number
  local_time?: string
  timezone?: string
  day_of_week?: number
  day_of_month?: number
}

export interface ScheduleWriteResult extends AnalysisSchedule {
  execution?: { execution_id: string; status: AnalysisRun['status'] }
  last_execution?: { execution_id: string; status: AnalysisRun['status'] }
}

/** One profile's slice of a run's paginated results (`results[]` on `RunResultsPage`). */
export interface ProfileResultEntry {
  profile_slug?: string
  status?: string
  credits?: { charged?: number; locked?: number }
  full_result?: { causal_output?: Record<string, unknown> }
  profile_config_snapshot?: { config?: import('./impact.types').ProfileConfigSnapshot } & Record<
    string,
    unknown
  >
  summary?: import('./impact.types').RunSummary
  warnings?: unknown[]
}

/** `GET /runs/{id}/results/` — `result` carries run-level metadata; `results` is per-profile. */
export interface RunResultsPage {
  count: number
  next: string | null
  previous?: string | null
  result?: AnalysisRun
  results: ProfileResultEntry[]
  summary_totals?: Record<string, number>
}

/**
 * `POST /runs/{id}/unlock/` — spends remaining credits on a partial run's locked rows. `unlocked`
 * is how many rows the balance actually covered; 0 means nothing could be unlocked right now.
 */
export interface RunUnlockResult {
  credits?: { unlocked?: number; charged?: number; locked?: number }
}

export interface MonthlyQuotaDto {
  monthly_limit: number
  used: number
  remaining: number
  next_reset_date?: string | null
}

export function toMonthlyQuota(dto: MonthlyQuotaDto): MonthlyQuota {
  return {
    limit: dto.monthly_limit,
    used: dto.used,
    remaining: dto.remaining,
    nextResetDate: dto.next_reset_date ?? null,
    isHasQuota: dto.remaining > 0,
  }
}
